import { useLoaderData, useNavigate } from 'react-router';
import { requireUser } from '../../lib/session.server';
import { apiClient } from '../../lib/api-client';
import type { LoaderFunctionArgs } from 'react-router';
import { 
  ChevronRight, 
  Target, 
  Settings,
  Bookmark,
  Award,
  Clock,
  BarChart3,
  Brain,
  Activity,
  Sparkles
} from 'lucide-react';
import { Card, CardContent, Button, Badge } from '../../components/ui';
import { StrategyBadge } from '../../components/jobs';



interface JobRecommendation {
  id: string;
  title: string;
  company: string;
  location: string;
  salary?: string;
  matchScore: number;
  attribution?: {
    strategy_key: string;
    confidence_score: number;
  };
  rationale?: string;
}

interface Application {
  id: string;
  jobTitle: string;
  company: string;
  status: string;
  appliedAt: string;
}

interface StrategyData {
  currentObjective: string;
  activeStrategies: number;
  weeklyRecommendations: number;
  bestPerformingStrategy: string;
}

interface DashboardData {
  user: { firstName: string; email: string };
  recommendations: JobRecommendation[];
  applications: Application[];
  strategy: StrategyData;
}

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await requireUser(request);
  
  // Load dashboard data with focus on AI recommendations
  const [recommendationsResponse, applicationsResponse, strategyResponse] = await Promise.allSettled([
    // Get top recommendations from Strategy Worker
    apiClient.post('/strategy/recommendations', { limit: 5 }).catch(() => ({ success: true, data: { recommendations: [] } })),
    // Get recent applications
    apiClient.get('/applications?page=1&perPage=3'),
    // Get strategy metrics
    apiClient.get('/strategy/metrics').catch(() => ({ success: true, data: {} }))
  ]);

  // Process recommendations data (Today's Top Picks)
  let recommendations: JobRecommendation[] = [];
  if (recommendationsResponse.status === 'fulfilled' && recommendationsResponse.value.success) {
    const recs = recommendationsResponse.value.data.recommendations || [];
    recommendations = recs.slice(0, 5).map((rec: any) => ({
      id: rec.id,
      title: rec.title,
      company: rec.company,
      location: rec.location || 'Remote',
      salary: rec.salary_range,
      matchScore: Math.round((rec.attribution?.confidence_score || 0.75) * 100),
      attribution: rec.attribution,
      rationale: rec.rationale
    }));
  }

  // Mock data for development
  if (recommendations.length === 0) {
    recommendations = [
      {
        id: '1',
        title: 'Senior SWE — Platform',
        company: 'TechCorp',
        location: 'Remote',
        salary: '$180–220k',
        matchScore: 89,
        attribution: { strategy_key: 'HIGH_COMP', confidence_score: 0.89 },
        rationale: 'Strong technical match with platform experience'
      },
      {
        id: '2',
        title: 'Frontend Lead',
        company: 'StartupCo',
        location: 'NYC',
        salary: '$140–180k',
        matchScore: 82,
        attribution: { strategy_key: 'EASY_WIN', confidence_score: 0.82 },
        rationale: 'Great culture fit and leadership opportunity'
      }
    ];
  }

  // Process applications data
  let applications: Application[] = [];
  if (applicationsResponse.status === 'fulfilled' && applicationsResponse.value.success) {
    const apps = applicationsResponse.value.data.applications || [];
    applications = apps.map((app: any) => ({
      id: app.id,
      jobTitle: app.job_title || 'Unknown Position',
      company: app.company || 'Unknown Company',
      status: app.status,
      appliedAt: new Date(app.created_at).toLocaleDateString()
    }));
  }

  // Process strategy data
  let strategy: StrategyData = {
    currentObjective: 'HIGH_COMP',
    activeStrategies: 6,
    weeklyRecommendations: 24,
    bestPerformingStrategy: 'RESUME_TAGS'
  };
  
  if (strategyResponse.status === 'fulfilled' && strategyResponse.value.success) {
    const data = strategyResponse.value.data;
    strategy = {
      currentObjective: data.currentObjective || 'HIGH_COMP',
      activeStrategies: data.activeStrategies || 6,
      weeklyRecommendations: data.weeklyRecommendations || 24,
      bestPerformingStrategy: data.bestPerformingStrategy || 'RESUME_TAGS'
    };
  }

  return {
    user,
    recommendations,
    applications,
    strategy
  };
}

export function DashboardPage() {
  const { user, recommendations, applications, strategy } = useLoaderData() as DashboardData;
  const navigate = useNavigate();


  return (
    <div className="container py-6">
      <div className="space-y-8">
        {/* AI-First Header */}
        <div className="space-y-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-foreground">
              Welcome back, {user?.firstName || 'User'}!
            </h1>
            <p className="text-muted-foreground text-lg mt-1">
              Continue reviewing recommendations
            </p>
          </div>
          
          {/* Header Actions */}
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => navigate('/discover')}
              className="gap-2"
            >
              <Sparkles className="h-4 w-4" />
              Open Feed
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/strategy')}
              className="gap-2"
            >
              <Settings className="h-4 w-4" />
              Tune Strategy
            </Button>
          </div>
        </div>

        {/* 8/4 Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column (8 cols) - Today's Top Picks + Recent Activity */}
          <div className="lg:col-span-8 space-y-8">
            {/* Today's Top Picks */}
            <Card className="border border-border hover:border-border-hover hover:shadow-card-hover transition-all">
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-primary rounded-xl">
                      <Brain className="h-5 w-5 text-white" />
                    </div>
                    <h2 className="text-xl font-semibold text-foreground">Today's Top Picks</h2>
                  </div>
                  <Button 
                    variant="subtle"
                    onClick={() => navigate('/discover')}
                    className="text-primary hover:text-primary/80 transition-colors group gap-1"
                  >
                    <span className="text-sm font-medium">View All</span>
                    <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>

                {recommendations.length > 0 ? (
                  <div className="space-y-4">
                    {recommendations.map((job) => (
                      <div 
                        key={job.id} 
                        className="group relative border border-border rounded-2xl p-4 hover:border-border-hover hover:shadow-card-hover focus:outline-none focus:ring-2 focus:ring-primary/40 active:bg-muted/50 transition-all cursor-pointer"
                        onClick={() => navigate(`/jobs/${job.id}`)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {job.attribution && (
                                <StrategyBadge 
                                  attribution={job.attribution} 
                                  size="sm"
                                />
                              )}
                              <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                                {job.company} — {job.title}
                              </h3>
                              <div className="flex items-center gap-1 bg-gradient-primary text-white px-2 py-1 rounded-full text-xs font-medium shadow-sm">
                                <Award className="h-3 w-3" />
                                {job.matchScore}% match
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                              <span>{job.location}</span>
                              {job.salary && (
                                <>
                                  <span>•</span>
                                  <span>{job.salary}</span>
                                </>
                              )}
                            </div>
                            {job.rationale && (
                              <p className="text-sm text-muted-foreground">
                                Why: {job.rationale}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); /* Save job */ }}>
                              <Bookmark className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-champagne-pink/10 rounded-2xl">
                    <Target className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">No recommendations ready</h3>
                    <p className="text-muted-foreground mb-4">
                      Complete your profile to get AI-powered job recommendations.
                    </p>
                    <Button onClick={() => navigate('/onboarding')} className="gap-2">
                      Complete Profile
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="border border-border hover:border-border-hover hover:shadow-card-hover transition-all">
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-secondary rounded-xl">
                      <Activity className="h-5 w-5 text-white" />
                    </div>
                    <h2 className="text-xl font-semibold text-foreground">Recent Activity</h2>
                  </div>
                  <Button 
                    variant="subtle"
                    onClick={() => navigate('/applications')}
                    className="text-primary hover:text-primary/80 transition-colors group gap-1"
                  >
                    <span className="text-sm font-medium">View All</span>
                    <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>

                {applications.length > 0 ? (
                  <div className="space-y-3">
                    {applications.map(app => {
                      const statusColors: Record<string, string> = {
                        'pending': 'bg-gradient-to-r from-yellow-400 to-orange-400',
                        'reviewing': 'bg-gradient-to-r from-blue-400 to-blue-500',
                        'interview': 'bg-gradient-to-r from-purple-400 to-purple-500',
                        'offer': 'bg-gradient-to-r from-green-400 to-green-500',
                        'rejected': 'bg-gradient-to-r from-red-400 to-red-500'
                      };
                      
                      return (
                        <div 
                          key={app.id} 
                          className="group border border-border rounded-xl p-4 hover:border-border-hover hover:shadow-card-hover focus:outline-none focus:ring-2 focus:ring-primary/40 active:bg-muted/50 transition-all cursor-pointer"
                          onClick={() => navigate(`/applications/${app.id}`)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium text-foreground group-hover:text-primary transition-colors">
                                  {app.jobTitle}
                                </h3>
                                <div className={`px-2 py-1 rounded-full text-xs font-medium text-white shadow-sm ${statusColors[app.status] || 'bg-gradient-secondary'}`}>
                                  {app.status}
                                </div>
                              </div>
                              <p className="text-sm text-muted-foreground">{app.company}</p>
                              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                                <Clock className="h-3 w-3" />
                                Applied {app.appliedAt}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-champagne-pink/10 rounded-2xl">
                    <Activity className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">No applications yet</h3>
                    <p className="text-muted-foreground mb-4">
                      Start applying to jobs to track your progress here.
                    </p>
                    <Button onClick={() => navigate('/discover')} className="gap-2">
                      Discover Jobs
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column (4 cols) - Strategy Snapshot */}
          <div className="lg:col-span-4">
            <Card className="border border-border hover:border-border-hover hover:shadow-card-hover transition-all">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-warm rounded-xl">
                    <BarChart3 className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground">Strategy Snapshot</h2>
                </div>

                <div className="space-y-6">
                  {/* Current Objective */}
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">Current Objective</h3>
                    <div className="flex items-center gap-2">
                      <Badge variant="info" className="text-sm">
                        {strategy.currentObjective.replace('_', ' ')}
                      </Badge>
                      <Button variant="subtle" size="sm" onClick={() => navigate('/strategy/objectives')}>
                        <Settings className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Active Strategies */}
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">Active Strategies</h3>
                    <div className="text-2xl font-bold text-foreground">
                      {strategy.activeStrategies}
                    </div>
                    <p className="text-xs text-muted-foreground">generating recommendations</p>
                  </div>

                  {/* Weekly Performance */}
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">This Week</h3>
                    <div className="text-2xl font-bold text-foreground">
                      {strategy.weeklyRecommendations}
                    </div>
                    <p className="text-xs text-muted-foreground">recommendations delivered</p>
                  </div>

                  {/* Best Performing Strategy */}
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-2">Top Performer</h3>
                    <Badge variant="positive" className="text-sm">
                      {strategy.bestPerformingStrategy.replace('_', ' ')}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">highest engagement rate</p>
                  </div>

                  {/* Strategy Actions */}
                  <div className="pt-4 border-t border-border">
                    <div className="space-y-2">
                      <Button variant="outline" className="w-full justify-start gap-2" onClick={() => navigate('/strategy/lab')}>
                        <Target className="h-4 w-4" />
                        View Strategy Lab
                      </Button>
                      <Button variant="outline" className="w-full justify-start gap-2" onClick={() => navigate('/analytics')}>
                        <BarChart3 className="h-4 w-4" />
                        Performance Analytics
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}